import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import { 
  Search,
  LayoutDashboard,
  Users,
  FileText,
  Building2,
  Receipt,
  Wallet,
  CreditCard,
  AlertTriangle,
  CheckSquare,
  Calculator,
  Settings,
  Calendar,
  Compass,
  Rocket,
  Shield,
  UserCog,
  Clock,
  Lightbulb,
  Target,
  TrendingUp,
  Heart,
  Zap,
  Lock,
  Headphones,
  BookOpen,
  ChevronRight,
  Mail
} from 'lucide-react';

// =====================================================
// CENTRO DE CONTROL · MANDO by Leapifyes
// Construido desde cero con visión SaaS profesional
// =====================================================

const SECCIONES = [
  // 1. BIENVENIDA A MANDO
  {
    id: 'bienvenida',
    titulo: 'Bienvenida a MANDO',
    icon: Compass,
    categoria: 'inicio',
    contenido: `
      <h3>Bienvenido a tu sistema de control</h3>
      <p><strong>MANDO</strong> no es una aplicación más. Es un sistema diseñado para que tengas el control total de tu negocio de reformas y construcción.</p>
      
      <div style="background: linear-gradient(135deg, #1e3a5f 0%, #2d4a6f 100%); border-radius: 12px; padding: 20px; margin: 20px 0; color: white;">
        <p style="margin: 0; font-size: 15px; font-weight: 500; line-height: 1.6;">
          "MANDO organiza tu negocio. Te quita ruido mental. Te ayuda a decidir mejor."
        </p>
        <p style="margin: 8px 0 0 0; font-size: 13px; opacity: 0.8;">— Filosofía MANDO · by Leapifyes</p>
      </div>

      <h4>¿Qué es MANDO?</h4>
      <p>MANDO es el sistema que te permite gestionar clientes, presupuestos, obras, facturas, gastos y cobros desde un solo lugar. Todo conectado, todo bajo control.</p>
      
      <h4>¿Qué puedes esperar?</h4>
      <ul>
        <li><strong>Claridad:</strong> Saber en todo momento cómo va tu negocio</li>
        <li><strong>Control:</strong> Nada se escapa, todo queda registrado</li>
        <li><strong>Decisiones:</strong> Datos que te ayudan a decidir mejor</li>
        <li><strong>Tranquilidad:</strong> Tu información segura y accesible</li>
      </ul>

      <h4>Cómo usar este Centro de Control</h4>
      <p>Este espacio está diseñado para acompañarte. Úsalo cuando:</p>
      <ul>
        <li>Empieces a usar una función nueva</li>
        <li>Tengas dudas sobre cómo hacer algo</li>
        <li>Quieras sacar más partido al sistema</li>
        <li>Necesites recordar dónde encontrar algo</li>
      </ul>
      <p>Navega por las secciones del menú lateral. Cada una explica una parte del sistema de forma clara y práctica.</p>
    `
  },

  // 2. PRIMEROS PASOS
  {
    id: 'primeros-pasos',
    titulo: 'Primeros Pasos',
    icon: Rocket,
    categoria: 'inicio',
    contenido: `
      <h3>Tu camino hacia el control total</h3>
      <p>Si acabas de empezar con MANDO, aquí tienes una guía clara para arrancar con buen pie.</p>

      <h4>Checklist de configuración inicial</h4>
      <div style="background: #f8fafc; border-radius: 8px; padding: 16px; margin: 16px 0;">
        <ul style="margin: 0; padding-left: 20px;">
          <li style="margin-bottom: 8px;"><strong>1. Configura tu empresa:</strong> Ve a Configuración de Empresa y completa tus datos fiscales</li>
          <li style="margin-bottom: 8px;"><strong>2. Personaliza documentos:</strong> Ajusta cómo se verán tus presupuestos y facturas</li>
          <li style="margin-bottom: 8px;"><strong>3. Añade tus primeros clientes:</strong> Empieza con 3-5 clientes activos</li>
          <li style="margin-bottom: 8px;"><strong>4. Crea tu primer presupuesto:</strong> Practica con un caso real</li>
          <li><strong>5. Explora el Dashboard:</strong> Familiarízate con tu centro de control</li>
        </ul>
      </div>

      <h4>Recomendación importante</h4>
      <p>No intentes configurar todo el primer día. MANDO está diseñado para crecer contigo. Empieza con lo básico y ve añadiendo según lo necesites.</p>

      <h4>Errores comunes al empezar</h4>
      <ul>
        <li><strong>No completar los datos de empresa:</strong> Tus facturas saldrán incompletas</li>
        <li><strong>No registrar gastos:</strong> No sabrás tu beneficio real</li>
        <li><strong>Dejar presupuestos en borrador:</strong> Pierde el hilo de lo pendiente</li>
        <li><strong>No usar el Dashboard:</strong> Es tu mejor herramienta de control diario</li>
      </ul>

      <h4>Tu rutina ideal con MANDO</h4>
      <p><strong>Cada mañana (2 minutos):</strong> Revisa el Dashboard. Mira qué facturas están pendientes, qué obras tienes activas y qué tareas necesitan atención.</p>
      <p><strong>Cada vez que hagas un gasto:</strong> Regístralo inmediatamente. Si lo dejas para después, se te olvidará.</p>
    `
  },

  // 3. GESTIÓN DE EMPRESA
  {
    id: 'gestion-empresa',
    titulo: 'Gestión de Empresa',
    icon: Building2,
    categoria: 'empresa',
    contenido: `
      <h3>Tu empresa, tu identidad</h3>
      <p>La configuración de tu empresa es la base de todo. Los datos que configures aquí aparecerán en tus documentos profesionales.</p>

      <h4>Configuración de empresa</h4>
      <p>Accede desde el menú lateral a <strong>Config. Empresa</strong> para configurar:</p>
      <ul>
        <li><strong>Nombre comercial:</strong> Cómo quieres que aparezca tu empresa</li>
        <li><strong>CIF/NIF:</strong> Tu identificación fiscal</li>
        <li><strong>Dirección completa:</strong> Domicilio fiscal</li>
        <li><strong>Teléfono y email:</strong> Datos de contacto</li>
      </ul>

      <h4>Personalización de documentos</h4>
      <p>En <strong>Configuración de Documentos</strong> puedes ajustar:</p>
      <ul>
        <li>Colores corporativos para tus PDFs</li>
        <li>Texto legal y condiciones</li>
        <li>Información de pago (IBAN, etc.)</li>
        <li>Porcentajes de IVA e IRPF</li>
      </ul>

      <h4>Plan y estado del servicio</h4>
      <p>Tu cuenta de MANDO puede estar en diferentes estados:</p>
      <ul>
        <li><strong>Trial:</strong> Periodo de prueba gratuita</li>
        <li><strong>Activo:</strong> Servicio completo activado</li>
      </ul>
      <p>Puedes ver tu estado actual en cualquier momento desde la configuración de tu cuenta.</p>

      <h4>¿Por qué es importante?</h4>
      <p>Una empresa bien configurada transmite profesionalidad. Tus clientes verán facturas y presupuestos con todos tus datos correctos, lo que genera confianza.</p>
    `
  },

  // 4. USUARIOS Y ROLES
  {
    id: 'usuarios-roles',
    titulo: 'Usuarios y Roles',
    icon: UserCog,
    categoria: 'empresa',
    contenido: `
      <h3>Tu equipo en MANDO</h3>
      <p>Si trabajas con más personas, puedes darles acceso a MANDO con diferentes niveles de permisos.</p>

      <h4>Tipos de usuario</h4>
      <div style="background: #f8fafc; border-radius: 12px; padding: 16px; margin: 16px 0;">
        <div style="display: flex; align-items: flex-start; gap: 12px; margin-bottom: 16px; padding-bottom: 16px; border-bottom: 1px solid #e2e8f0;">
          <div style="width: 36px; height: 36px; background: #7c3aed; border-radius: 8px; display: flex; align-items: center; justify-content: center; flex-shrink: 0;">
            <span style="color: white; font-weight: bold; font-size: 14px;">A</span>
          </div>
          <div>
            <p style="margin: 0 0 4px 0; font-weight: 600; color: #1e293b;">Administrador</p>
            <p style="margin: 0; font-size: 13px; color: #64748b;">Control total. Puede hacer cualquier cosa: crear, editar, eliminar, gestionar usuarios y configurar la empresa.</p>
          </div>
        </div>
        <div style="display: flex; align-items: flex-start; gap: 12px; margin-bottom: 16px; padding-bottom: 16px; border-bottom: 1px solid #e2e8f0;">
          <div style="width: 36px; height: 36px; background: #3b82f6; border-radius: 8px; display: flex; align-items: center; justify-content: center; flex-shrink: 0;">
            <span style="color: white; font-weight: bold; font-size: 14px;">U</span>
          </div>
          <div>
            <p style="margin: 0 0 4px 0; font-weight: 600; color: #1e293b;">Usuario</p>
            <p style="margin: 0; font-size: 13px; color: #64748b;">Puede crear y editar clientes, presupuestos, obras, facturas y gastos. No puede gestionar usuarios ni configurar la empresa.</p>
          </div>
        </div>
        <div style="display: flex; align-items: flex-start; gap: 12px;">
          <div style="width: 36px; height: 36px; background: #64748b; border-radius: 8px; display: flex; align-items: center; justify-content: center; flex-shrink: 0;">
            <span style="color: white; font-weight: bold; font-size: 14px;">L</span>
          </div>
          <div>
            <p style="margin: 0 0 4px 0; font-weight: 600; color: #1e293b;">Solo Lectura</p>
            <p style="margin: 0; font-size: 13px; color: #64748b;">Puede ver toda la información pero no puede crear ni modificar nada. Ideal para consultas.</p>
          </div>
        </div>
      </div>

      <h4>Cómo invitar usuarios</h4>
      <p>Si eres Administrador:</p>
      <ol>
        <li>Ve a <strong>Usuarios</strong> en el menú lateral</li>
        <li>Pulsa <strong>Invitar Usuario</strong></li>
        <li>Introduce nombre, email y selecciona el rol</li>
        <li>Copia el enlace de invitación y envíalo</li>
      </ol>
      <p>El nuevo usuario recibirá un enlace para crear su contraseña y acceder.</p>

      <h4>Buenas prácticas</h4>
      <ul>
        <li>Da el mínimo permiso necesario para cada persona</li>
        <li>Revisa periódicamente quién tiene acceso</li>
        <li>Elimina usuarios que ya no trabajen contigo</li>
        <li>Usa "Solo Lectura" para personas que solo necesitan consultar</li>
      </ul>
    `
  },

  // 5. DASHBOARD Y CONTROL
  {
    id: 'dashboard',
    titulo: 'Dashboard y Control',
    icon: LayoutDashboard,
    categoria: 'operaciones',
    contenido: `
      <h3>Tu centro de mando diario</h3>
      <p>El Dashboard es la primera pantalla que ves al entrar. Está diseñado para darte una foto completa de tu negocio en segundos.</p>

      <h4>Indicadores clave que encontrarás</h4>
      <ul>
        <li><strong>Cobros pendientes:</strong> Cuánto dinero te deben tus clientes</li>
        <li><strong>Presupuestos sin respuesta:</strong> Propuestas esperando decisión</li>
        <li><strong>Obras activas:</strong> Trabajos en curso ahora mismo</li>
        <li><strong>Próximos pagos:</strong> Lo que tienes que pagar pronto</li>
        <li><strong>Gastos del mes:</strong> Control de salidas de dinero</li>
      </ul>

      <h4>Gráficos de tendencia</h4>
      <p>Los gráficos te muestran la evolución de los últimos 6 meses:</p>
      <ul>
        <li><strong>Ingresos vs Gastos:</strong> Cómo fluye tu dinero</li>
        <li><strong>Margen mensual:</strong> Tu beneficio real cada mes</li>
        <li><strong>Objetivos:</strong> Comparación con tus metas</li>
      </ul>

      <h4>Qué mirar cada día</h4>
      <div style="background: #fef3c7; border-radius: 8px; padding: 16px; margin: 16px 0; border-left: 4px solid #f59e0b;">
        <p style="margin: 0; font-size: 14px; color: #92400e;">
          <strong>Rutina de 2 minutos:</strong> Cada mañana, abre el Dashboard. Mira los números rojos (vencidos, urgentes). Esas son tus prioridades del día.
        </p>
      </div>

      <h4>Señales de alerta</h4>
      <p>Presta atención especial cuando veas:</p>
      <ul>
        <li>Facturas vencidas que llevan tiempo sin cobrarse</li>
        <li>Gastos del mes muy superiores a ingresos</li>
        <li>Muchos presupuestos sin respuesta (¿necesitas hacer seguimiento?)</li>
        <li>Obras sin actualizar estado desde hace tiempo</li>
      </ul>
    `
  },

  // 6. CLIENTES
  {
    id: 'clientes',
    titulo: 'Clientes',
    icon: Users,
    categoria: 'operaciones',
    contenido: `
      <h3>El corazón de tu negocio</h3>
      <p>Tus clientes son la razón de ser de tu empresa. En MANDO, cada cliente tiene su ficha con toda la información que necesitas.</p>

      <h4>Información de cada cliente</h4>
      <ul>
        <li>Nombre o razón social</li>
        <li>Teléfono y email de contacto</li>
        <li>Dirección completa</li>
        <li>NIF/CIF (para facturas)</li>
        <li>Notas internas</li>
      </ul>

      <h4>Para qué sirve tener clientes bien organizados</h4>
      <ul>
        <li>Al crear presupuestos o facturas, seleccionas el cliente y sus datos se rellenan automáticamente</li>
        <li>Puedes ver el historial completo: todos los presupuestos, obras y facturas de ese cliente</li>
        <li>El buscador te permite encontrar cualquier cliente en segundos</li>
      </ul>

      <h4>Buenas prácticas</h4>
      <ul>
        <li><strong>Siempre email:</strong> Te servirá para enviar documentos</li>
        <li><strong>NIF completo:</strong> Imprescindible para facturas válidas</li>
        <li><strong>Notas útiles:</strong> Apunta cómo prefiere que le contactes, horarios, etc.</li>
        <li><strong>Actualiza datos:</strong> Si un cliente cambia de teléfono o dirección, actualízalo</li>
      </ul>

      <h4>Errores a evitar</h4>
      <ul>
        <li>No duplicar clientes (usa el buscador antes de crear uno nuevo)</li>
        <li>No dejar campos vacíos que luego necesitarás</li>
        <li>No usar abreviaturas confusas en los nombres</li>
      </ul>
    `
  },

  // 7. PRESUPUESTOS
  {
    id: 'presupuestos',
    titulo: 'Presupuestos',
    icon: FileText,
    categoria: 'operaciones',
    contenido: `
      <h3>La puerta de entrada a cada trabajo</h3>
      <p>Un buen presupuesto es el primer paso hacia un trabajo rentable. MANDO te ayuda a crear presupuestos profesionales que impresionan y convierten.</p>

      <h4>Estados de un presupuesto</h4>
      <div style="display: grid; grid-template-columns: repeat(2, 1fr); gap: 8px; margin: 16px 0;">
        <div style="background: #f1f5f9; padding: 12px; border-radius: 8px;">
          <p style="margin: 0; font-weight: 600; color: #64748b;">Borrador</p>
          <p style="margin: 4px 0 0 0; font-size: 13px; color: #94a3b8;">Lo estás preparando</p>
        </div>
        <div style="background: #dbeafe; padding: 12px; border-radius: 8px;">
          <p style="margin: 0; font-weight: 600; color: #3b82f6;">Enviado</p>
          <p style="margin: 4px 0 0 0; font-size: 13px; color: #60a5fa;">El cliente lo tiene</p>
        </div>
        <div style="background: #dcfce7; padding: 12px; border-radius: 8px;">
          <p style="margin: 0; font-weight: 600; color: #22c55e;">Aceptado</p>
          <p style="margin: 4px 0 0 0; font-size: 13px; color: #4ade80;">¡Trabajo conseguido!</p>
        </div>
        <div style="background: #fee2e2; padding: 12px; border-radius: 8px;">
          <p style="margin: 0; font-weight: 600; color: #ef4444;">Rechazado</p>
          <p style="margin: 4px 0 0 0; font-size: 13px; color: #f87171;">No salió adelante</p>
        </div>
      </div>

      <h4>Estructura profesional</h4>
      <p>Cada presupuesto puede tener partidas con:</p>
      <ul>
        <li>Descripción clara del trabajo</li>
        <li>Unidad de medida (m², unidades, horas...)</li>
        <li>Cantidad y precio unitario</li>
      </ul>
      <p>MANDO calcula automáticamente subtotales, IVA y total final.</p>

      <h4>Funciones que te ahorran tiempo</h4>
      <ul>
        <li><strong>Duplicar:</strong> Copia un presupuesto existente para trabajos similares</li>
        <li><strong>PDF profesional:</strong> Genera documentos listos para enviar</li>
        <li><strong>Versionado:</strong> El sistema guarda el número de versión</li>
      </ul>

      <h4>Consejos para presupuestar mejor</h4>
      <ul>
        <li>Usa la <strong>Calculadora ROI</strong> antes de dar un precio</li>
        <li>Detalla bien las partidas: el cliente entenderá mejor el valor</li>
        <li>No dejes presupuestos en "Enviado" demasiado tiempo sin hacer seguimiento</li>
      </ul>
    `
  },

  // 8. OBRAS
  {
    id: 'obras',
    titulo: 'Obras',
    icon: Building2,
    categoria: 'operaciones',
    contenido: `
      <h3>El ciclo de vida de cada trabajo</h3>
      <p>Cuando un presupuesto se acepta, nace una obra. En MANDO, cada obra es un proyecto completo con su propia vida, gastos y seguimiento.</p>

      <h4>Estados de una obra</h4>
      <ul>
        <li><strong>Pendiente:</strong> Aprobada pero aún no has empezado</li>
        <li><strong>En curso:</strong> Estás trabajando activamente en ella</li>
        <li><strong>Pausada:</strong> Temporalmente detenida por algún motivo</li>
        <li><strong>Finalizada:</strong> Trabajo completado con éxito</li>
        <li><strong>Cancelada:</strong> Se ha anulado definitivamente</li>
      </ul>

      <h4>Información que registras</h4>
      <ul>
        <li>Cliente y presupuesto de origen</li>
        <li>Dirección donde se realiza el trabajo</li>
        <li>Fechas de inicio y fin previstas</li>
        <li>Presupuesto económico asignado</li>
        <li>Notas y observaciones</li>
      </ul>

      <h4>Control y seguimiento</h4>
      <p>Lo más importante de las obras es vincular los gastos. Cada vez que compras material, pagas a un trabajador o tienes cualquier coste, lo asocias a la obra correspondiente.</p>
      <p>Así sabrás exactamente <strong>cuánto te está costando cada trabajo</strong> y si estás ganando o perdiendo dinero.</p>

      <h4>Calendario de obras</h4>
      <p>Usa el <strong>Calendario</strong> para ver visualmente cuándo tienes programado cada trabajo. Los colores te indican el estado de cada obra.</p>

      <h4>Rentabilidad por proyecto</h4>
      <p>Cuando termines una obra, compara:</p>
      <ul>
        <li>Lo que presupuestaste</li>
        <li>Lo que gastaste realmente</li>
        <li>Lo que facturaste</li>
      </ul>
      <p>Esta información es oro para mejorar tus futuros presupuestos.</p>
    `
  },

  // 9. FACTURAS Y PAGOS
  {
    id: 'facturas-pagos',
    titulo: 'Facturas y Pagos',
    icon: Receipt,
    categoria: 'operaciones',
    contenido: `
      <h3>El momento de cobrar</h3>
      <p>Las facturas son el documento oficial para cobrar tu trabajo. MANDO genera facturas profesionales y te ayuda a controlar los cobros.</p>

      <h4>Estados de una factura</h4>
      <ul>
        <li><strong>Borrador:</strong> Aún la estás preparando</li>
        <li><strong>Pendiente:</strong> Emitida, esperando pago</li>
        <li><strong>Enviada:</strong> Ya se la mandaste al cliente</li>
        <li><strong>Cobrada:</strong> ¡Dinero recibido!</li>
        <li><strong>Vencida:</strong> Ha pasado la fecha límite de pago</li>
      </ul>

      <h4>PDF profesional</h4>
      <p>Cada factura genera un PDF con:</p>
      <ul>
        <li>Tus datos de empresa (configurados previamente)</li>
        <li>Datos fiscales del cliente</li>
        <li>Desglose de conceptos</li>
        <li>Base, IVA y total</li>
        <li>Información de pago</li>
      </ul>

      <h4>Control de cobros</h4>
      <p>En el Dashboard verás siempre:</p>
      <ul>
        <li>Total pendiente de cobro</li>
        <li>Facturas vencidas (las que deberían estar ya pagadas)</li>
      </ul>
      <p>Marca las facturas como "Cobrada" en cuanto recibas el pago para tener tus números actualizados.</p>

      <h4>Pagos a proveedores</h4>
      <p>En la sección <strong>Pagos</strong> controlas lo que tú debes pagar:</p>
      <ul>
        <li>A quién debes pagar</li>
        <li>Cuánto y cuándo vence</li>
        <li>Estado: pendiente o pagado</li>
      </ul>
      <p>El Dashboard te avisa de los pagos próximos a vencer.</p>
    `
  },

  // 10. GASTOS Y RENTABILIDAD
  {
    id: 'gastos-rentabilidad',
    titulo: 'Gastos y Rentabilidad',
    icon: Wallet,
    categoria: 'operaciones',
    contenido: `
      <h3>Saber si ganas dinero de verdad</h3>
      <p>Facturar mucho no significa ganar mucho. La clave está en controlar los gastos. MANDO te ayuda a saber tu beneficio real.</p>

      <h4>Tipos de gastos</h4>
      <ul>
        <li><strong>Material:</strong> Compras de materiales de construcción</li>
        <li><strong>Mano de obra:</strong> Pagos a trabajadores y subcontratas</li>
        <li><strong>Transporte:</strong> Desplazamientos, alquiler de vehículos</li>
        <li><strong>Otros:</strong> Cualquier otro coste</li>
      </ul>

      <h4>La regla de oro</h4>
      <div style="background: #fef3c7; border-radius: 8px; padding: 16px; margin: 16px 0; border-left: 4px solid #f59e0b;">
        <p style="margin: 0; font-size: 14px; color: #92400e;">
          <strong>Registra cada gasto el mismo día que lo haces.</strong> Si lo dejas para después, se te olvidará y tus números estarán mal.
        </p>
      </div>

      <h4>Cómo saber si ganas dinero</h4>
      <p>La fórmula es simple:</p>
      <p style="background: #f1f5f9; padding: 12px; border-radius: 8px; text-align: center; font-weight: 600;">
        Beneficio = Lo que cobras − Lo que gastas
      </p>
      <p>Si no registras los gastos, este número será mentira.</p>

      <h4>Errores habituales</h4>
      <ul>
        <li>No registrar gastos pequeños (se acumulan)</li>
        <li>No asociar gastos a la obra correcta</li>
        <li>Olvidar gastos de desplazamiento</li>
        <li>No contar la mano de obra propia</li>
      </ul>

      <h4>Análisis de rentabilidad</h4>
      <p>Al finalizar cada obra, revisa:</p>
      <ul>
        <li>¿Gasté más o menos de lo presupuestado?</li>
        <li>¿Dónde se fueron los sobrecostes?</li>
        <li>¿Qué puedo hacer diferente la próxima vez?</li>
      </ul>
    `
  },

  // 11. CALENDARIO
  {
    id: 'calendario',
    titulo: 'Calendario y Planificación',
    icon: Calendar,
    categoria: 'operaciones',
    contenido: `
      <h3>Tu agenda visual de obras</h3>
      <p>El calendario te muestra todas tus obras de forma visual para que puedas planificar mejor tu tiempo.</p>

      <h4>Vistas disponibles</h4>
      <ul>
        <li><strong>Vista mensual:</strong> Panorama general del mes</li>
        <li><strong>Vista semanal:</strong> Detalle de la semana actual</li>
      </ul>

      <h4>Código de colores</h4>
      <div style="display: grid; grid-template-columns: repeat(2, 1fr); gap: 8px; margin: 16px 0;">
        <div style="display: flex; align-items: center; gap: 8px;">
          <span style="width: 12px; height: 12px; background: #3b82f6; border-radius: 50%;"></span>
          <span style="font-size: 14px;">En curso</span>
        </div>
        <div style="display: flex; align-items: center; gap: 8px;">
          <span style="width: 12px; height: 12px; background: #22c55e; border-radius: 50%;"></span>
          <span style="font-size: 14px;">Finalizada</span>
        </div>
        <div style="display: flex; align-items: center; gap: 8px;">
          <span style="width: 12px; height: 12px; background: #f59e0b; border-radius: 50%;"></span>
          <span style="font-size: 14px;">Pausada</span>
        </div>
        <div style="display: flex; align-items: center; gap: 8px;">
          <span style="width: 12px; height: 12px; background: #94a3b8; border-radius: 50%;"></span>
          <span style="font-size: 14px;">Pendiente</span>
        </div>
      </div>

      <h4>Cómo usarlo</h4>
      <ul>
        <li>Haz clic en cualquier obra para ver sus detalles</li>
        <li>Usa las flechas para navegar entre meses o semanas</li>
        <li>Identifica rápidamente periodos con mucha o poca carga de trabajo</li>
      </ul>

      <h4>Planificación inteligente</h4>
      <p>El calendario te ayuda a:</p>
      <ul>
        <li>No solapar demasiadas obras</li>
        <li>Identificar huecos para nuevos trabajos</li>
        <li>Prever periodos de mucha actividad</li>
      </ul>
    `
  },

  // 12. CALCULADORA ROI
  {
    id: 'calculadora-roi',
    titulo: 'Calculadora ROI',
    icon: Calculator,
    categoria: 'herramientas',
    contenido: `
      <h3>Decide antes de presupuestar</h3>
      <p>La Calculadora ROI te ayuda a saber si un trabajo te sale rentable <strong>antes</strong> de comprometerte con un precio.</p>

      <h4>Para qué sirve</h4>
      <p>Introduces los datos estimados de un trabajo y el sistema te dice:</p>
      <ul>
        <li><strong>Coste total:</strong> Lo que te va a costar hacerlo</li>
        <li><strong>Precio sugerido:</strong> Lo que deberías cobrar</li>
        <li><strong>Beneficio neto:</strong> Lo que te quedará</li>
        <li><strong>ROI:</strong> Retorno de la inversión en porcentaje</li>
        <li><strong>Precio por m²:</strong> Para comparar con el mercado</li>
      </ul>

      <h4>Datos que introduces</h4>
      <ul>
        <li>Tipo de trabajo (reforma, baño, cocina, pintura...)</li>
        <li>Metros cuadrados</li>
        <li>Coste estimado de materiales</li>
        <li>Horas de mano de obra</li>
        <li>Precio por hora</li>
        <li>Otros gastos previstos</li>
      </ul>

      <h4>Cómo interpretarla</h4>
      <p>La calculadora te da recomendaciones automáticas:</p>
      <ul>
        <li><strong>Verde:</strong> Buen margen, adelante</li>
        <li><strong>Amarillo:</strong> Margen justo, revisa costes</li>
        <li><strong>Rojo:</strong> Margen bajo o negativo, reconsidera</li>
      </ul>

      <h4>Decisiones que te ayuda a tomar</h4>
      <ul>
        <li>¿Acepto este trabajo al precio que pide el cliente?</li>
        <li>¿Cuánto es lo mínimo que puedo cobrar?</li>
        <li>¿Vale la pena hacer este tipo de trabajos?</li>
      </ul>
    `
  },

  // 13. TRIAL Y PLANES
  {
    id: 'trial-planes',
    titulo: 'Trial, Planes y Estado',
    icon: Lock,
    categoria: 'cuenta',
    contenido: `
      <h3>Tu cuenta en MANDO</h3>
      <p>MANDO ofrece un periodo de prueba gratuito para que puedas explorar el sistema antes de decidir.</p>

      <h4>Cómo funciona el trial</h4>
      <ul>
        <li><strong>Duración:</strong> 7 días de acceso completo</li>
        <li><strong>Sin tarjeta:</strong> No necesitas datos de pago para empezar</li>
        <li><strong>Funcionalidades completas:</strong> Prueba todo el sistema</li>
      </ul>

      <h4>¿Qué pasa al finalizar el trial?</h4>
      <div style="background: #ecfdf5; border-radius: 8px; padding: 16px; margin: 16px 0; border-left: 4px solid #22c55e;">
        <p style="margin: 0 0 8px 0; font-weight: 600; color: #166534;">Tranquilidad total</p>
        <p style="margin: 0; font-size: 14px; color: #166534;">
          <strong>Tus datos NO se borran.</strong> Podrás seguir accediendo para consultar tu información. Solo se bloquean las acciones de creación y edición hasta que actives el servicio.
        </p>
      </div>

      <h4>Qué se bloquea y qué no</h4>
      <p><strong>Puedes seguir haciendo:</strong></p>
      <ul>
        <li>Ver el Dashboard y todos tus datos</li>
        <li>Consultar clientes, obras, facturas...</li>
        <li>Acceder a tu información histórica</li>
      </ul>
      <p><strong>Se bloquea temporalmente:</strong></p>
      <ul>
        <li>Crear nuevos clientes, presupuestos, obras...</li>
        <li>Exportar a PDF</li>
        <li>Invitar nuevos usuarios</li>
      </ul>

      <h4>Cómo activar el servicio</h4>
      <p>Para continuar con acceso completo:</p>
      <ol>
        <li>Haz clic en "Activar MANDO" cuando aparezca el aviso</li>
        <li>Contacta con el equipo de Leapifyes para configurar tu plan</li>
        <li>Una vez activado, recuperas todas las funciones inmediatamente</li>
      </ol>

      <h4>Garantía legal</h4>
      <p>Tus datos te pertenecen. Nunca se eliminan sin tu consentimiento explícito.</p>
    `
  },

  // 14. SOPORTE
  {
    id: 'soporte',
    titulo: 'Soporte y Acompañamiento',
    icon: Headphones,
    categoria: 'cuenta',
    contenido: `
      <h3>Estamos aquí para ayudarte</h3>
      <p>Leapifyes no es solo tecnología. Es un equipo de personas que quiere que tu negocio funcione mejor.</p>

      <h4>Cómo contactar</h4>
      <div style="background: #f8fafc; border-radius: 12px; padding: 20px; margin: 16px 0;">
        <div style="display: flex; align-items: center; gap: 12px; margin-bottom: 12px;">
          <div style="width: 40px; height: 40px; background: #1e3a5f; border-radius: 8px; display: flex; align-items: center; justify-content: center;">
            <span style="color: white; font-size: 18px;">✉️</span>
          </div>
          <div>
            <p style="margin: 0; font-weight: 600; color: #1e293b;">Email de soporte</p>
            <p style="margin: 4px 0 0 0; font-size: 14px; color: #3b82f6;">soporte@leapifyes.com</p>
          </div>
        </div>
        <p style="margin: 0; font-size: 13px; color: #64748b;">Respondemos en menos de 24 horas laborables</p>
      </div>

      <h4>Cuándo contactar</h4>
      <ul>
        <li>Algo no funciona como debería</li>
        <li>Necesitas ayuda para configurar algo</li>
        <li>Tienes una duda que no encuentras en este Centro</li>
        <li>Quieres sugerir una mejora</li>
      </ul>

      <h4>Cuándo pedir acompañamiento</h4>
      <p>Si prefieres una ayuda más personalizada:</p>
      <ul>
        <li>Configuración inicial guiada</li>
        <li>Formación para tu equipo</li>
        <li>Revisión de cómo estás usando el sistema</li>
        <li>Consultoría de procesos</li>
      </ul>
      <p>Escríbenos y te contamos las opciones.</p>

      <h4>Nuestra filosofía</h4>
      <div style="background: linear-gradient(135deg, #1e3a5f 0%, #2d4a6f 100%); border-radius: 12px; padding: 20px; margin: 16px 0; color: white;">
        <p style="margin: 0; font-size: 15px; line-height: 1.6;">
          "Acompañamos, no imponemos. Nuestro objetivo es que MANDO se adapte a tu forma de trabajar, no al revés."
        </p>
        <p style="margin: 12px 0 0 0; font-size: 13px; opacity: 0.8;">— Equipo Leapifyes</p>
      </div>
    `
  }
];

// FAQ actualizado sin referencias a SERAMA
const FAQ = [
  {
    pregunta: '¿Cómo creo mi primer presupuesto?',
    respuesta: 'Ve a Presupuestos → Nuevo Presupuesto. Selecciona un cliente (o créalo primero), añade un título, y luego ve añadiendo las partidas. El sistema calcula todo automáticamente. Cuando esté listo, descarga el PDF y envíalo.'
  },
  {
    pregunta: '¿Cómo paso un presupuesto aceptado a obra?',
    respuesta: 'Primero cambia el estado del presupuesto a "Aceptado". Luego ve a Obras → Nueva Obra y selecciona ese presupuesto. Los datos quedarán vinculados para tu control.'
  },
  {
    pregunta: '¿Cómo sé si una obra me está dando beneficio?',
    respuesta: 'Registra todos los gastos asociados a esa obra. MANDO comparará automáticamente lo presupuestado con lo gastado y te mostrará el margen real. Sin gastos registrados, no hay control real.'
  },
  {
    pregunta: '¿Puedo modificar una factura ya emitida?',
    respuesta: 'Las facturas en estado "Borrador" se pueden modificar libremente. Una vez emitidas, lo correcto legalmente es crear una factura rectificativa. Puedes usar "Duplicar" y ajustar.'
  },
  {
    pregunta: '¿Qué pasa cuando termina mi periodo de prueba?',
    respuesta: 'Tus datos se mantienen seguros. Podrás seguir consultando toda tu información. Solo se bloquea la creación y edición hasta que actives el servicio. Sin sorpresas, sin borrados.'
  },
  {
    pregunta: '¿Puedo añadir más usuarios a mi cuenta?',
    respuesta: 'Sí. Si eres Administrador, ve a Usuarios e invita a las personas de tu equipo. Puedes asignarles diferentes roles según lo que necesiten hacer.'
  },
  {
    pregunta: '¿Cómo exporto mis datos?',
    respuesta: 'En el Dashboard y en cada sección encontrarás opciones de exportación. Puedes exportar a Excel (clientes, presupuestos, obras, facturas, gastos) o a PDF (presupuestos y facturas individuales).'
  },
  {
    pregunta: '¿El IVA se calcula automáticamente?',
    respuesta: 'Sí. En Configuración de Documentos defines el IVA por defecto. En cada presupuesto o factura puedes ajustarlo si necesitas un tipo diferente.'
  }
];

// Categorías para el sidebar
const CATEGORIAS = [
  { id: 'inicio', label: 'Empezar', icon: Compass },
  { id: 'empresa', label: 'Tu Empresa', icon: Building2 },
  { id: 'operaciones', label: 'Operaciones', icon: Zap },
  { id: 'herramientas', label: 'Herramientas', icon: Calculator },
  { id: 'cuenta', label: 'Tu Cuenta', icon: Shield },
];

const CentroAyuda = () => {
  const { companyName } = useAuth();
  const [searchTerm, setSearchTerm] = useState('');
  const [activeSection, setActiveSection] = useState('bienvenida');

  const filteredSecciones = SECCIONES.filter(s => 
    s.titulo.toLowerCase().includes(searchTerm.toLowerCase()) ||
    s.contenido.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredFAQ = FAQ.filter(f =>
    f.pregunta.toLowerCase().includes(searchTerm.toLowerCase()) ||
    f.respuesta.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const currentSection = SECCIONES.find(s => s.id === activeSection);

  // Agrupar secciones por categoría
  const seccionesPorCategoria = CATEGORIAS.map(cat => ({
    ...cat,
    secciones: filteredSecciones.filter(s => s.categoria === cat.id)
  }));

  return (
    <div className="space-y-6 animate-fade-in" data-testid="centro-control-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Centro de Control</h1>
          <p className="text-slate-500 text-sm mt-1">Todo lo que necesitas para dominar MANDO · by Leapifyes</p>
        </div>
        
        {/* Search */}
        <div className="relative w-full sm:w-80">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Buscar en el Centro de Control..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-9"
            data-testid="search-centro-control"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Sidebar - Categorías y Secciones */}
        <div className="lg:col-span-1 space-y-4">
          {seccionesPorCategoria.map((categoria) => (
            categoria.secciones.length > 0 && (
              <div key={categoria.id}>
                <div className="flex items-center gap-2 px-2 mb-2">
                  <categoria.icon className="w-4 h-4 text-slate-400" />
                  <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
                    {categoria.label}
                  </p>
                </div>
                <div className="space-y-1">
                  {categoria.secciones.map((seccion) => {
                    const Icon = seccion.icon;
                    const isActive = activeSection === seccion.id;
                    return (
                      <button
                        key={seccion.id}
                        onClick={() => setActiveSection(seccion.id)}
                        data-testid={`nav-${seccion.id}`}
                        className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-left transition-colors ${
                          isActive 
                            ? 'bg-slate-900 text-white' 
                            : 'text-slate-600 hover:bg-slate-100'
                        }`}
                      >
                        <Icon className="w-4 h-4 flex-shrink-0" />
                        <span className="text-sm truncate">{seccion.titulo}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )
          ))}
        </div>

        {/* Main Content */}
        <div className="lg:col-span-3 space-y-6">
          {/* Current Section Content */}
          {currentSection && (
            <Card className="border-slate-200">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <currentSection.icon className="w-5 h-5 text-slate-500" />
                  {currentSection.titulo}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div 
                  className="prose prose-slate prose-sm max-w-none
                    prose-headings:text-slate-900 prose-headings:font-semibold
                    prose-h3:text-lg prose-h3:mt-0 prose-h3:mb-3
                    prose-h4:text-base prose-h4:mt-5 prose-h4:mb-2
                    prose-p:text-slate-600 prose-p:leading-relaxed prose-p:my-3
                    prose-ul:text-slate-600 prose-ul:my-3
                    prose-ol:text-slate-600 prose-ol:my-3
                    prose-li:my-1
                    prose-strong:text-slate-900"
                  dangerouslySetInnerHTML={{ __html: currentSection.contenido }}
                />
              </CardContent>
            </Card>
          )}

          {/* Quick Tips - Solo mostrar si no hay búsqueda activa */}
          {!searchTerm && (
            <Card className="border-slate-200 bg-gradient-to-br from-slate-50 to-slate-100">
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2 text-slate-700">
                  <Lightbulb className="w-5 h-5 text-amber-500" />
                  Claves para sacar partido a MANDO
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {[
                    { icon: Target, text: 'Revisa el Dashboard cada mañana (2 min)' },
                    { icon: Clock, text: 'Registra gastos el mismo día' },
                    { icon: TrendingUp, text: 'Usa la calculadora antes de presupuestar' },
                    { icon: Heart, text: 'Mantén los datos de clientes actualizados' },
                  ].map((tip, idx) => (
                    <div key={idx} className="flex items-center gap-3 text-sm text-slate-700 bg-white p-3 rounded-lg">
                      <tip.icon className="w-4 h-4 text-slate-500 flex-shrink-0" />
                      <span>{tip.text}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* FAQ */}
          <Card className="border-slate-200">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <BookOpen className="w-5 h-5 text-slate-500" />
                Preguntas Frecuentes
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Accordion type="single" collapsible className="w-full">
                {filteredFAQ.map((faq, idx) => (
                  <AccordionItem key={idx} value={`faq-${idx}`}>
                    <AccordionTrigger className="text-left text-sm font-medium hover:no-underline">
                      {faq.pregunta}
                    </AccordionTrigger>
                    <AccordionContent className="text-sm text-slate-600 leading-relaxed">
                      {faq.respuesta}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </CardContent>
          </Card>

          {/* Contacto */}
          <Card className="border-slate-200 bg-gradient-to-br from-slate-900 to-slate-800 text-white">
            <CardContent className="pt-6">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-white/10 rounded-lg flex items-center justify-center">
                    <Headphones className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="font-semibold text-white">¿Necesitas ayuda personalizada?</p>
                    <p className="text-sm text-slate-300 mt-1">El equipo de Leapifyes está aquí para acompañarte</p>
                  </div>
                </div>
                <a 
                  href="mailto:soporte@leapifyes.com"
                  className="inline-flex items-center gap-2 px-4 py-2 bg-white text-slate-900 rounded-lg font-medium text-sm hover:bg-slate-100 transition-colors"
                >
                  <Mail className="w-4 h-4" />
                  Contactar Soporte
                </a>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default CentroAyuda;
